import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import { Modal, Header } from '@trinity/chat-components/components/Modal/index.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import Button from '@trinity/chat-components/components/presentation/Button/index.js';
import useRenameFileMutation from '@/containers/Workspace.File/hooks/useRenameFile';

type Props = {
  fileId: number;
  workspaceId: number;
  name: string;
} & Pick<ModalProps, 'open' | 'onClose'>;

export const RenameFileModal = ({
  fileId,
  workspaceId,
  name: initialName,
  onClose,
  open,
}: Props) => {

  const [name, setName] = useState(initialName);
  const mutation = useRenameFileMutation();

  const handleSave = useCallback(() => {
    return mutation.mutateAsync({
      fileId,
      workspaceId,
      name,
    }, {
      onSuccess: () => {
        onClose();
      },
    });
  }, [fileId, mutation, name, onClose, workspaceId]);

  const Footer = useCallback(() => {
    return (
      <ButtonSet>
        <StyledButton
          disabled={mutation.isPending}
          color="destructive"
          onClick={onClose}
          variant="brick">
          Cancel
        </StyledButton>
        <StyledButton
          disabled={mutation.isPending}
          color="primary"
          onClick={handleSave}
          variant="brick">
          Save
        </StyledButton>
      </ButtonSet>
    );
  }, [handleSave, mutation.isPending, onClose]);

  return (
    <Modal open={open} onClose={onClose}>
      <Header>Rename File</Header>
      <TextField
        sx={{ marginBottom: 2 }}
        autoFocus
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        variant='standard'
        placeholder="Enter new file name"
      />
      <Footer />
    </Modal>
  );
};

const StyledButton = styled(Button)({
  minWidth: 130,
});